import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  centavos,
  formatMoney,
  hoy,
  newId,
  parseMoney,
  subMoney,
  type Centavos,
  type CuentaBancoCordobas,
} from '@abastece/shared';
import type { Abono, Cliente } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas } from '../lib/bancos.js';
import { sesionAbierta } from '../lib/caja.js';
import { emitirYVer } from '../lib/emitir.js';
import { otrosUsos, useUsosDeReferencias, yaPago } from '../lib/referencias.js';
import { usePuede } from '../lib/sesion.js';
import { textoAbono } from '../lib/fiado.js';
import { imprimirTexto } from '../lib/imprimir.js';
import { enlaceWhatsApp } from '../lib/recibo.js';
import { useT, type Clave } from '../i18n/index.js';
import { Campo } from './Campo.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { Ico, type NombreIcono } from './Iconos.js';
import { Modal } from './Modal.js';

type FormaAbono = Abono['formaPago'];

/** A tender is a thing on the counter, so these carry pictures, as the till's do. */
const FORMAS: { forma: FormaAbono; etiqueta: Clave; icono: NombreIcono }[] = [
  { forma: 'efectivo_cordobas', etiqueta: 'pago.efectivo_cordobas', icono: 'caja' },
  { forma: 'transferencia', etiqueta: 'pago.transferencia', icono: 'transferencias' },
  { forma: 'billetera', etiqueta: 'pago.billetera', icono: 'billetera' },
];

/**
 * Taking an abono: from the client's page, or from the till with the client on the sale.
 *
 * Cash goes into this device's open till — it is the drawer the money is physically going into,
 * and the corte has to expect it. With no till open, cash has nowhere to go and the sheet says
 * so; a transfer or a wallet payment needs none. Either way the screen says where, before it is
 * recorded.
 */
export function AbonoModal({
  cliente,
  negocio,
  onCerrar,
}: {
  cliente: Cliente;
  negocio: string;
  onCerrar: () => void;
}) {
  const t = useT();
  // Null: no till open on this device. Undefined: still looking.
  const caja = useLiveQuery(async () => (await sesionAbierta()) ?? null, []);
  const saldo = cliente.saldo ?? (0 as Centavos);
  const [monto, setMonto] = useState(saldo > 0 ? String(saldo / 100) : '');
  const [forma, setForma] = useState<FormaAbono>('efectivo_cordobas');
  const [referencia, setReferencia] = useState('');
  const [nota, setNota] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<{ texto: string } | null>(null);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The córdoba account a transfer came into, once picked: where transfers go until then. */
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBancoCordobas | null>(null);
  const cuenta = cuentaEnCordobas(config, cuentaElegida);

  const valor = parseMoney(monto);
  // Cash goes into the open till, where the corte expects it; with none, it has nowhere to go.
  const sinCaja = forma === 'efectivo_cordobas' && caja === null;
  /**
   * A transfer or a wallet payment names its receipt, as at the till: the reference is what the
   * queue checks against the bank, and what gives away a screenshot shown twice. Taken by somebody
   * who does not confirm them, it waits in «Transferencias» until somebody sees the money.
   */
  const usos = useUsosDeReferencias();
  const confirma = usePuede('confirmar_transferencia');
  const faltaReferencia = forma !== 'efectivo_cordobas' && !referencia.trim();
  const repetida = forma !== 'efectivo_cordobas' ? otrosUsos(usos, referencia)[0] : undefined;
  const valido = valor !== null && valor > 0 && caja !== undefined && !sinCaja && !faltaReferencia;
  const quedaria = valor !== null && valor > 0 ? subMoney(saldo, valor) : saldo;
  const aLaGaveta = forma === 'efectivo_cordobas' && !!caja;

  async function registrar() {
    if (!valido || ocupado) return;
    setOcupado(true);
    setError(null);
    const abonoId = newId();
    try {
      const { rechazo } = await emitirYVer('abono.registrado', {
        abonoId,
        clienteId: cliente.id,
        fecha: hoy(),
        monto: valor,
        formaPago: forma,
        ...(aLaGaveta && caja ? { sesionId: caja.id } : {}),
        ...(forma === 'transferencia' ? { cuenta } : {}),
        ...(forma !== 'efectivo_cordobas' && referencia.trim() ? { referencia: referencia.trim() } : {}),
        ...(nota.trim() ? { nota: nota.trim() } : {}),
      });
      // Refused (the till closed a moment ago on another screen, say): say why, never a receipt.
      if (rechazo || !(await db.abonos.get(abonoId))) {
        setOcupado(false);
        setError(rechazo ?? t('abono.noSePudo'));
        return;
      }
    } catch (e) {
      // Thrown, it used to leave the button on «Guardando» for good.
      setOcupado(false);
      setError(e instanceof Error ? e.message : t('abono.noSePudo'));
      return;
    }
    const despues = await db.clientes.get(cliente.id);
    setHecho({ texto: textoAbono(negocio, despues ?? cliente, { monto: valor, fecha: hoy() }) });
  }

  if (hecho) {
    return (
      <Modal titulo={t('abono.listo')} onCerrar={onCerrar}>
        <pre className="tabular mb-4 overflow-x-auto rounded-xl bg-slate-950 p-4 text-xs leading-relaxed text-slate-300">
          {hecho.texto}
        </pre>
        <div className="flex flex-col gap-3">
          <a
            className="btn-secundario w-full"
            href={enlaceWhatsApp(hecho.texto, cliente.telefono ?? undefined)}
            target="_blank"
            rel="noreferrer"
          >
            <Ico icono="whatsapp" />
            {t('abono.enviar')}
          </a>
          <button type="button" className="btn-secundario w-full" onClick={() => imprimirTexto(hecho.texto)}>
            <Ico icono="imprimir" />
            {t('comun.imprimir')}
          </button>
          <button type="button" className="btn-primario w-full" onClick={onCerrar} data-guia="abono-listo">
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal titulo={t('abono.titulo', { nombre: cliente.nombre })} onCerrar={onCerrar}>
      <div className="flex flex-col gap-4" data-guia="abono-form">
        <Campo
          etiqueta={t('abono.monto')}
          inputMode="decimal"
          className="tabular text-lg"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          ayuda={
            valor !== null && valor > 0
              ? quedaria >= 0
                ? t('abono.quedaria', { monto: formatMoney(quedaria) })
                : t('abono.quedaAFavor', { monto: formatMoney(centavos(-quedaria)) })
              : undefined
          }
        />

        <div>
          <span className="etiqueta">{t('abono.forma')}</span>
          <div className="grid grid-cols-3 gap-2">
            {FORMAS.map((f) => (
              <button
                key={f.forma}
                type="button"
                onClick={() => setForma(f.forma)}
                className={`flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl px-2 py-1.5 text-xs font-medium transition ${
                  forma === f.forma ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                }`}
              >
                <Ico icono={f.icono} className="size-5" />
                <span className="text-center leading-tight">{t(f.etiqueta)}</span>
              </button>
            ))}
          </div>
          <p className={`mt-2 text-xs ${sinCaja ? 'text-amber-300' : 'text-slate-400'}`}>
            {forma === 'efectivo_cordobas'
              ? t(aLaGaveta ? 'abono.aLaGaveta' : 'abono.sinCaja')
              : !confirma
                ? t('abono.porConfirmar')
                : t(forma === 'transferencia' ? 'abono.alBanco' : 'abono.aLaBilletera')}
          </p>
        </div>

        {forma === 'transferencia' && (
          <ElegirCuenta valor={cuenta} onCambiar={(c) => setCuentaElegida(c as CuentaBancoCordobas)} etiqueta={t('bancos.entraA')} />
        )}

        {forma !== 'efectivo_cordobas' && (
          <div>
            <Campo
              etiqueta={t('cobro.referenciaObligatoria')}
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              autoComplete="off"
              maxLength={80}
            />
            {repetida && <p className="mt-1.5 text-sm text-rose-300">{t(yaPago(repetida).clave, yaPago(repetida).valores)}</p>}
          </div>
        )}
        <Campo
          maxLength={200}
          etiqueta={t('comun.nota')}
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          autoComplete="off"
        />

        {error && <p className="text-sm text-rose-400">{error}</p>}
        <button type="button" className="btn-primario" disabled={!valido || ocupado} onClick={() => void registrar()}>
          <Ico icono="confirmar" />
          {ocupado ? t('comun.guardando') : t('abono.registrar')}
        </button>
      </div>
    </Modal>
  );
}
